import { Calendar, Clock, Globe, Sparkles, Users } from "lucide-react";

import { Page } from "../components/Page";

/* ─────────────── helpers ─────────────── */

function VoiceLine({ context, line }: { context: string; line: string }) {
  return (
    <div className="border-t pt-4 border-rule first:border-t-0 first:pt-0">
      <p className="eyebrow">{context}</p>
      <p className="mt-2 font-display text-[20px] italic text-ink">"{line}"</p>
    </div>
  );
}

function ChipRow({ items }: { items: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((i) => (
        <span
          key={i}
          className="rounded-full border px-3 py-1 font-body text-[12px] tracking-wide text-ink border-rule"
        >
          {i}
        </span>
      ))}
    </div>
  );
}

function FactRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ size?: number; color?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 border-b pb-3 border-rule last:border-b-0 last:pb-0">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{ background: "rgba(160,90,31,0.12)" }}
      >
        <Icon size={18} color="var(--amber)" />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="font-body text-[11px] tracking-[0.22em] uppercase text-ink-soft">
          {label}
        </span>
        <span className="font-display text-[17px] text-ink">{value}</span>
      </div>
    </div>
  );
}

/* ─────────────── case study card ─────────────── */

interface CaseStudyProps {
  number: string;
  title: string;
  oneLiner: string;
  audience: string;
  positioning: string;
  facts: {
    icon: React.ComponentType<{ size?: number; color?: string }>;
    label: string;
    value: string;
  }[];
  voice: { context: string; line: string }[];
  channels: string[];
  adSrc: string;
  adCaption: string;
}

function CaseStudy(props: CaseStudyProps) {
  return (
    <section className="mt-16 first:mt-0">
      <div className="flex items-baseline gap-4">
        <span className="font-display text-[28px] tabular-nums text-amber">
          {props.number}
        </span>
        <span className="h-px flex-1 bg-[color:var(--rule)]" />
        <span className="font-flair text-[20px] text-amber">
          {props.oneLiner}
        </span>
      </div>

      <h2
        className="display mt-6 text-ink"
        style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
      >
        {props.title}
      </h2>

      <div className="mt-10 grid gap-8 md:grid-cols-[1fr_440px]">
        {/* LEFT — story */}
        <div className="flex flex-col gap-10">
          <div>
            <p className="eyebrow">Audience</p>
            <span className="hairline" />
            <p className="mt-5 font-body text-[16px] leading-relaxed text-ink">
              {props.audience}
            </p>
          </div>

          <div>
            <p className="eyebrow">Positioning</p>
            <span className="hairline" />
            <p className="mt-5 font-display text-[20px] leading-[1.35] italic text-ink">
              {props.positioning}
            </p>
          </div>

          <div>
            <p className="eyebrow">Voice in context</p>
            <span className="hairline" />
            <div className="mt-5 flex flex-col gap-1">
              {props.voice.map((v) => (
                <VoiceLine key={v.context} {...v} />
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow">Where it shows up</p>
            <span className="hairline" />
            <div className="mt-5">
              <ChipRow items={props.channels} />
            </div>
          </div>
        </div>

        {/* RIGHT — facts + ad */}
        <div className="flex flex-col gap-6">
          <div className="surface p-6" style={{ background: "var(--paper)" }}>
            <p className="eyebrow">The basics</p>
            <span className="hairline" />
            <div className="mt-5 flex flex-col gap-3">
              {props.facts.map((f) => (
                <FactRow key={f.label} {...f} />
              ))}
            </div>
          </div>

          <figure
            className="surface overflow-hidden"
            style={{ background: "var(--bg-soft)" }}
          >
            <div className="aspect-square w-full">
              <img
                src={props.adSrc}
                alt={props.adCaption}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <figcaption className="p-4">
              <p className="font-body text-[12px] leading-relaxed text-ink-soft">
                {props.adCaption}
              </p>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── page ─────────────── */

export function UseCases() {
  return (
    <Page
      number="09"
      title="Use cases"
      eyebrow="The brand in practice"
      intro={
        <>
          Two campaigns the brand has shipped — the recurring weekly Sunday
          session in Sydney, and the eleven-day structured challenge. Each is
          shown end-to-end so the system can be read as a whole: who it's for,
          how we talk about it, what it looks like in market.
        </>
      }
    >
      <CaseStudy
        number="01"
        title="Meditation for Inner Peace"
        oneLiner="The weekly Sunday sit"
        audience="First-time and curious sitters. Likely arriving through a Facebook ad, our Sydney Meetup page, or a friend. May be skeptical, may not call themselves spiritual, may have tried a few apps and bounced. They're not buying a product — they're considering an hour on a Sunday."
        positioning="A simple meditation, taught carefully — ten minutes morning, ten minutes evening. One Sunday session a week, free, in person or online."
        facts={[
          { icon: Calendar, label: "Cadence", value: "Every Sunday" },
          { icon: Clock, label: "Time", value: "10:00 am AEST" },
          {
            icon: Users,
            label: "Format",
            value: "In person · online via Meetup",
          },
          { icon: Sparkles, label: "Cost", value: "Always free" },
          { icon: Globe, label: "Door", value: "meetup.com/vy-australia" },
        ]}
        voice={[
          {
            context: "Headline",
            line: "Pause the noise. Reconnect with yourself.",
          },
          {
            context: "Sub",
            line: "Helps reduce stress, calm the mind, and experience deep inner stillness through a simple guided meditation.",
          },
          {
            context: "Invite",
            line: "Come sit with us this Sunday — Sydney CBD, 10am, free.",
          },
          {
            context: "After the sit",
            line: "Thanks for joining. We'll see you next Sunday.",
          },
        ]}
        channels={[
          "Facebook ads",
          "Meetup event page",
          "vyaustralia.org hero",
          "WhatsApp share cards",
          "Sunday session reminders",
        ]}
        adSrc="/applications/ad-pause-the-noise.png"
        adCaption="Square campaign ad — runs on Facebook for new-attendee acquisition. Pause-the-noise headline, the Sunday session card on the right, online stamp."
      />

      <div className="mt-20 mb-20 border-t border-rule" />

      <CaseStudy
        number="02"
        title="The 11-Day Meditation Challenge"
        oneLiner="A structured journey"
        audience="Returning practitioners who want shape to their week, plus curious newcomers who want a beginning and an end. They've often dabbled in meditation and want a frame that helps it stick. Engaged enough to sign up, but allergic to anything that feels like a cult or a course."
        positioning="Eleven days. Twenty minutes a day. A structured way to settle into the practice — guided morning and evening, with a community sitting alongside."
        facts={[
          { icon: Calendar, label: "Dates", value: "Sun 7 — Wed 17 Jun 2026" },
          {
            icon: Clock,
            label: "Daily commitment",
            value: "10 min morning + 10 min evening",
          },
          {
            icon: Users,
            label: "Format",
            value: "Online portal · community stats",
          },
          { icon: Sparkles, label: "Cost", value: "Always free" },
          { icon: Globe, label: "Door", value: "vyaustralia.org/challenge" },
        ]}
        voice={[
          { context: "Headline", line: "Eleven days. Twenty minutes a day." },
          {
            context: "Sub",
            line: "A structured way to settle into the practice — guided morning and evening, with a community sitting alongside.",
          },
          {
            context: "Day-1 nudge",
            line: "Day 1 of 11 — ten minutes is enough today.",
          },
          {
            context: "Mid-stream",
            line: "Day 4 of 11. The streak isn't the point; showing up is.",
          },
          {
            context: "Completion",
            line: "You've sat eleven days. Keep going — the door stays open.",
          },
        ]}
        channels={[
          "vyaustralia.org/challenge hero",
          "Portal dashboard",
          "Sign-up confirmation email",
          "Daily morning + evening nudges",
          "Completion certificate (post-Day 11)",
        ]}
        adSrc="/applications/ad-eleven-days.png"
        adCaption="Square campaign ad — runs on Facebook + LinkedIn for Challenge sign-ups. Day-strip visual makes the structure read at a glance; Day 1 marked 'today' for the morning of launch."
      />
    </Page>
  );
}
